import React, { useMemo } from 'react';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import { TEXT_STYLES, TextVariant } from '@components/shared/design/Typography';
import ArticleCarousel from '@components/vitals/ArticleCarousel';
import { PageSlice, SliceVitalsCarouselDetails } from '@contracts';
import CarouselChevron from '@images/carousel-chevron.svg';
import { useArticles } from '@utils/useArticles';
import useCarousel from '@utils/useCarousel';
import Colors from './shared/Colors';
import FadeIn from './shared/FadeIn';
import LinkButton from './shared/LinkButton';

interface Props {
  articleIds?: string;
  hideControls?: boolean;
  noGradient?: boolean;
  background?: string;
  slice?: PageSlice;
}

const VitalsCarousel: React.FC<Props> = ({ articleIds, hideControls = false, noGradient = false, background, slice }) => {
  const { t } = useTranslation();
  const { carouselRef, carouselNext, carouselPrevious, previousDisabled, nextDisabled } = useCarousel();
  const sliceDetails = slice?.primary as SliceVitalsCarouselDetails | undefined;
  const articles = useArticles(articleIds || sliceDetails?.article_ids_string);
  const containerBackground =
    !noGradient && !sliceDetails?.background?.text && !background
      ? 'linear-gradient(180deg, #e5f2e350 31.31%, rgba(229, 242, 227, 0) 96.86%)'
      : background || sliceDetails?.background?.text || 'none';

  return (
    <FadeIn>
      <Container $background={containerBackground}>
        <Header>
          <Heading>{t(sliceDetails?.heading?.text || 'components.stay-informed-vitals')}</Heading>
          {!hideControls && (
            <Controls>
              <CarouselPrevious disabled={previousDisabled} onClick={carouselPrevious} aria-label="Carousel previous page">
                <CarouselChevron />
              </CarouselPrevious>
              <CarouselNext disabled={nextDisabled} onClick={carouselNext} aria-label="Carousel next page">
                <CarouselChevron />
              </CarouselNext>
              <LinkButton to="/vitals/" color={Colors.navyBlue}>
                {t('components.view-all-articles')}
              </LinkButton>
            </Controls>
          )}
        </Header>
        {useMemo(
          () => (
            <ArticleCarousel articles={articles} carouselRef={carouselRef} background={sliceDetails?.background?.text} />
          ),
          [articles]
        )}
        <MobileLinkButton to="/vitals/" color={Colors.navyBlue}>
          {t('components.view-all-articles')}
        </MobileLinkButton>
      </Container>
    </FadeIn>
  );
};

export default VitalsCarousel;

const Container = styled.section<{ $background: string }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  margin: auto;
  padding: 100px 30px;
  overflow: hidden;
  background: ${(p) => p.$background};
  background-size: 100% 188px;
  background-repeat: no-repeat;
  mix-blend-mode: normal;

  @media (max-width: 1024px) {
    padding: 50px 0;
    align-items: center;
  }
`;

const Header = styled.header`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  max-width: 1200px;
  width: 100%;
  margin: 0 auto 48px;

  @media (max-width: 1024px) {
    padding: 0 32px;
  }
`;

const Heading = styled.div`
  color: ${Colors.navyBlue};
  ${TEXT_STYLES[TextVariant.Heading4]};
  text-align: left;

  @media (max-width: 450px) {
    ${TEXT_STYLES[TextVariant.Heading5]};
  }
`;

const Controls = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  @media (max-width: 1024px) {
    display: none;
  }
`;

const CarouselNext = styled.button`
  background: none;
  border: 0;
  height: 22px;
  width: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 30px;
  cursor: pointer;
  padding: 0;

  path {
    fill: ${Colors.navyBlue};
  }

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }

  @media (max-width: 768px) {
    padding: 0 4px;
  }
`;

const CarouselPrevious = styled(CarouselNext)`
  transform: rotate(180deg);
  margin-right: 20px;
`;

const MobileLinkButton = styled(LinkButton)`
  display: none;

  @media (max-width: 1024px) {
    display: flex;
    margin: 24px 0 0;
  }
`;
